<template>
    <div>
        <admin-page-header title="질문지 관리" contained/>

        <template v-if="is_template_loaded === true">
            <b-container class="mb-45">
                <b-row>
                    <b-col cols="12" xl="6">
                        <h4 class="fw-600 mb-0"> 생성/사용중인 질문지 </h4>
                    </b-col>
                    <b-col cols="12" xl="6">
                        <div class="d-flex flex-column align-items-end">
                            <div class="d-flex justify-content-end mb-3">
                                <b-btn variant="dark-border"
                                       class="f-105 fw-500 mr-2"
                                       size="sm"
                                       :disabled="is_parsing_criteriaDefinition"
                                       @click="newTemplate"
                                >
                                    <fa icon="plus" class="mr-2"></fa>
                                    새 질문지
                                </b-btn>
                                <b-btn variant="dark-border"
                                       class="f-105 fw-500"
                                       size="sm"
                                       :to="{ name: 'ManagingCriteria' }"
                                       :disabled="is_parsing_criteriaDefinition"
                                >
                                    <fa icon="flag" class="mr-2"></fa>
                                    Criteria 관리
                                </b-btn>
                            </div>
                            <div class="d-flex justify-content-end">
                                <b-btn variant="dark-border"
                                       class="fw-500 mr-2"
                                       size="sm"
                                       :disabled="is_parsing_criteriaDefinition"
                                       @click="parseCriteriaDefinition()"
                                >
                                    <template v-if="is_parsing_criteriaDefinition">
                                        <b-spinner small class="mr-1"></b-spinner>
                                        파싱 중…
                                    </template>
                                    <template v-else>
                                        <fa icon="tasks" class="mr-1"></fa>
                                        전체 질문지 답변 파싱 및 저장
                                    </template>
                                </b-btn>
                                <b-btn variant="dark-border"
                                       class="fw-500"
                                       size="sm"
                                       :to="{ name: 'ManagingParserValues' }"
                                       :disabled="is_parsing_criteriaDefinition"
                                >
                                    <fa icon="flag" class="mr-1"></fa>
                                    Parser Value 관리
                                </b-btn>
                            </div>
                        </div>

                    </b-col>
                </b-row>
            </b-container>

            <b-container v-if="all_templates.length" class="py-0 py-md-3">
                <!-- DS 표 규칙: 사용중 · 사용 종료 두 표는 같은 칸 · 같은 폭(한 벌만 그린다). 행 44 고정 — 칸마다 한 줄, 넘치면 줄이고 전체는 title -->
                <div v-for="section in template_sections" :key="section.key" :class="{ 'mb-5': section.key === 'on_going' }">
                    <h4 v-if="section.key === 'archived'" class="mb-5 gray-500 fw-600">
                        <span
                            v-b-tooltip.hover
                            title="캠페인이 종료되어 사용중이지 않은 질문지 입니다."
                        >
                            사용 종료된 질문지
                        </span>
                    </h4>
                    <b-table-simple v-if="section.items.length" hover responsive
                                    table-class="ds-table ds-table-fixed template-list-table">
                        <b-thead>
                            <b-tr>
                                <b-th class="ds-cell-no" style="width: 56px;">No.</b-th>
                                <b-th>템플릿명</b-th>
                                <b-th>연결된 캠페인</b-th>
                                <b-th style="width: 168px;">최종편집일</b-th>
                                <b-th class="text-right" style="width: 156px;">관리</b-th>
                            </b-tr>
                        </b-thead>
                        <b-tbody>
                            <!-- 사용 종료 행은 글자를 흐리게 -->
                            <b-tr v-for="(item, index) in section.items"
                                  :key="item.template_id"
                                  :class="{ 'ds-sub': section.key === 'archived' }"
                            >
                                <b-td class="ds-cell-no">{{ index + 1 }}</b-td>
                                <!-- 메인 칸 = 편집 화면 링크(연필과 같은 곳 · 새 탭이 된다). 권한이 없거나 파싱 중이면 글자만 -->
                                <b-td>
                                    <span class="ds-ellipsis" :class="{ 'fw-500': item.type === 'owner' }" :title="item.template_name">
                                        <router-link v-if="checkAllowed(item.type, is_admin, 4) === true && !is_parsing_criteriaDefinition"
                                                     :to="editTemplateRoute(item)" class="ds-detail-link"
                                                     @click.native="prepareEditTemplate(item)">{{ item.template_name }}</router-link>
                                        <template v-else>{{ item.template_name }}</template>
                                    </span>
                                </b-td>
                                <!-- 첫 캠페인 + '외 N개' — 전체 목록(이름 · 상태)은 '외 N개' 를 누르면 뜨는 창과 title 로 -->
                                <b-td>
                                    <div v-if="check_multiple_campaign(item.campaigns)" class="d-flex align-items-center">
                                        <span class="ds-ellipsis template-campaign-name" :title="campaignTitle(item)">{{ item.campaign_name }}</span>
                                        <span class="ds-sub flex-shrink-0 ml-1">
                                            <button :id="`template-campaigns-${section.key}-${item.template_id}`" type="button" class="ds-detail-link"
                                                    :aria-label="`${item.template_name} 연결된 캠페인 ${item.campaigns.length}개 보기`">외 {{ item.campaigns.length - 1 }}개</button>
                                        </span>
                                        <b-popover :target="`template-campaigns-${section.key}-${item.template_id}`"
                                                   triggers="click blur" placement="bottom" boundary="viewport">
                                            <ul class="list-unstyled mb-0">
                                                <li v-for="(campaign, campaign_index) in item.campaigns" :key="campaign_index"
                                                    class="d-flex justify-content-between align-items-center py-1">
                                                    <span class="mr-3">{{ campaign.campaign_name }}</span>
                                                    <span class="ds-status" :class="`ds-status-${variantCampaignState[campaign.campaign_state] || 'secondary'}`">{{ campaign.campaign_state }}</span>
                                                </li>
                                            </ul>
                                        </b-popover>
                                    </div>
                                    <span v-else-if="item.campaign_name" class="ds-ellipsis" :title="item.campaign_name">{{ item.campaign_name }}</span>
                                    <span v-else class="ds-sub">-</span>
                                </b-td>
                                <b-td class="ds-cell-nowrap">
                                    <span class="ds-ellipsis" :title="item.time_stamp">{{ item.time_stamp }}</span>
                                </b-td>
                                <!-- 행 액션(아이콘 28): 수정/편집 · 번역 수정 · 복제 · 삭제 -->
                                <b-td>
                                    <div v-if="checkAllowed(item.type, is_admin, 4) === true" class="d-flex justify-content-end" style="gap: 4px">
                                        <button type="button" class="ds-row-icon-btn"
                                                :disabled="is_parsing_criteriaDefinition"
                                                :aria-label="`${item.template_name} 수정/편집`" title="수정/편집"
                                                @click.stop="editTemplate(item)">
                                            <AppIcon name="pencil"/>
                                        </button>
                                        <b-link class="ds-row-icon-btn"
                                                :disabled="is_parsing_criteriaDefinition"
                                                :to="{ name: 'EditingTemplateTranslation', params: { template_code: item.template_code } }"
                                                :aria-label="`${item.template_name} 번역 수정`" title="번역 수정"><AppIcon name="language"/></b-link>
                                        <button type="button" class="ds-row-icon-btn"
                                                :disabled="is_parsing_criteriaDefinition || copying_template_code !== null"
                                                :aria-label="`${item.template_name} 복제`" title="복제"
                                                @click.stop="copyTemplate(item)">
                                            <b-spinner v-if="copying_template_code === item.template_code" small/>
                                            <AppIcon v-else name="copy"/>
                                        </button>
                                        <button type="button" class="ds-row-icon-btn"
                                                :disabled="is_parsing_criteriaDefinition || deleting_template_id !== null"
                                                :aria-label="`${item.template_name} 삭제`" title="삭제"
                                                @click.stop="deleteTemplate(item)">
                                            <AppIcon name="trash"/>
                                        </button>
                                    </div>
                                    <div v-else class="ds-sub text-right">권한 없음</div>
                                </b-td>
                            </b-tr>
                        </b-tbody>
                    </b-table-simple>
                    <div v-else class="py-2 text-center gray-600 bgray-200">사용 중인 질문지가 없습니다. 새 질문지를 만들어 주세요.</div>

                    <hr v-if="section.key === 'on_going'" class="border-0 pb-5">
                </div>
                <hr class="border-0 bottom-space">
            </b-container>
            <b-container v-else class="px-0 px-md-5 py-0 py-md-3">
                <div class="mt-2 py-2 text-center gray-600 bgray-200"> 저장된 질문지가 없습니다. 새 질문지를 만들어 주세요.</div>
            </b-container>
        </template>
        <PageEmptyComponent v-else-if="load_error" :error="load_error" @retry="readTemplates(user_id)" />
        <template v-else>
            <b-container class="px-0 px-md-5 py-0 py-md-3">
                <b-skeleton-table
                    :rows="4"
                    :columns="4"
                    :table-props="{ bordered: false, striped: false }"
                ></b-skeleton-table>
            </b-container>
        </template>
    </div>
</template>

<script>
import {mapState, mapMutations} from 'vuex';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';
import {getErrorMessage} from '@core/utils/api/errorMessage';


export default {
    components: {PageEmptyComponent},
    props: {
        initiate_list: {type: Boolean, default: true}
    },
    data() {
        return {
            all_templates: [],
            is_template_loaded: false,
            load_error: '',
            deletable_item: '',
            is_parsing_criteriaDefinition: false,
            deleting_template_id: null,
            copying_template_code: null,
            variantCampaignState:{
                '답변중': 'warning',
                '생성/편집': 'primary',
                '종료': 'secondary'
            }
        };
    },
    computed: {
        ...mapState(['user_id', 'is_admin']),
        on_going_templates() {
            let temp_on_going = this.all_templates.filter(item => item.campaign_state !== '종료');
            return temp_on_going.sort((b, a) => new Date(a.time_stamp).getTime() - new Date(b.time_stamp).getTime());
        },
        archived_templates() {
            let temp_archived = this.all_templates.filter(item => item.campaign_state === '종료');
            return temp_archived.sort((b, a) => new Date(a.time_stamp).getTime() - new Date(b.time_stamp).getTime());
        },
        check_multiple_campaign() {
            return (campaigns) => {
                return campaigns?.length > 1;
            };
        },
        // 사용중 표는 늘 두고(비면 안내 문구), 사용 종료 표는 질문지가 있을 때만
        template_sections() {
            const sections = [{key: 'on_going', items: this.on_going_templates}];
            if (this.archived_templates.length) sections.push({key: 'archived', items: this.archived_templates});
            return sections;
        }
    },
    async created() {
        await this.readTemplates(this.user_id);
        this.update_answer_company({});
    },
    methods: {
        ...mapMutations('questions', ['change_edit_mode', 'update_answer_company']),
        async readTemplates(user_id) {
            try {
                this.load_error = '';
                const response = await this.$axios.get('/read_template_list', {
                    params: {user_id: user_id, mode: 'qesg_assessment'}
                });

                let result = response.data.templates;
                let all_templates = [];

                for (var i = 0; i < result.length; i++) {
                    const {
                        template_id, template_name, user_id, template_code, type, time_stamp,
                        campaign_name, campaign_state, template_contents, campaigns
                    } = result[i];

                    let parsed_template = {
                        template_id, template_name, user_id, template_code, type, time_stamp,
                        campaign_name, campaign_state, campaigns
                    };

                    parsed_template.template_contents = template_contents !== ''
                        ? JSON.parse(result[i].template_contents)
                        : [];

                    all_templates.push(parsed_template);
                }
                this.all_templates = all_templates.filter(item => item.user_id !== null);

                this.is_template_loaded = true;
            } catch (error) {
                console.log(' ~ error => ', error);
                // 401 은 axios 인터셉터가 토큰 갱신·로그인 화면 이동을 맡는다
                if (error.response && error.response.status === 401) return;
                this.load_error = getErrorMessage(error, '질문지 목록을 불러오지 못했습니다.');
            }
        },
        editTemplateRoute(template) {
            return {name: 'EditingTemplate', query: {value_new: false, template_id: template.template_code}};
        },
        // 이름 링크 · 연필 모두 편집 모드를 켜고 목록 값을 먼저 넣는다(편집 화면은 template_id 로 다시 읽는다)
        prepareEditTemplate(template) {
            this.change_edit_mode(true);
            this.$store.commit('update_template_tree', template);
        },
        editTemplate(new_template) {
            this.prepareEditTemplate(new_template);
            this.$router.push(this.editTemplateRoute(new_template));
        },
        campaignTitle(item) {
            return (item.campaigns || []).map(c => `${c.campaign_name} (${c.campaign_state || '-'})`).join('\n');
        },
        async deleteTemplate(item) {
            if (this.deleting_template_id !== null) return;
            const _h = this.$createElement;
            const _campaigns = item.campaigns?.length ? item.campaigns : (item.campaign_name ? [{campaign_name: item.campaign_name, campaign_state: item.campaign_state}] : []);
            const _ongoing = _campaigns.filter(c => c.campaign_state !== '종료').length;
            const result = await this.$showModal({
                type: 'confirm',
                state: 'error',
                title: '질문지 삭제',
                message: _h('div', {}, [
                    _h('p', {class: 'mb-1'}, `'${item.template_name}' 질문지를 삭제합니다.`),
                    _campaigns.length
                        ? _h('div', {class: 'mb-1'}, [
                            _h('p', {class: 'mb-0'}, `연결된 캠페인 ${_campaigns.length}개`),
                            _h('ul', {class: 'mb-1 pl-4'}, _campaigns.map(c => _h('li', {}, `${c.campaign_name} (${c.campaign_state || '-'})`))),
                        ])
                        : null,
                    _ongoing ? _h('p', {class: 'mb-1 text-danger'}, `진행 중인 캠페인 ${_ongoing}개가 이 질문지를 사용하고 있습니다.`) : null,
                    _h('p', {class: 'mb-0'}, '목록에서 사라지며 화면에서 되돌릴 수 없습니다. 연결된 캠페인은 그대로 동작합니다.'),
                ]),
            });
            if (!result) return;
            this.deleting_template_id = item.template_id;
            try {
                let formData = new FormData();
                formData.append('template_id', item.template_id);
                await this.$axios.post('/delete_template', formData);
                this.$EventBus.$emit('toast-success', '알림', `${item.template_name}을 성공적으로 삭제하였습니다.`);

                const i = this.all_templates.findIndex((x) => x.template_id === item.template_id);
                if (i > -1) this.all_templates.splice(i, 1);
            } catch (error) {
                if (error?.response?.status === 401) return;
                this.$EventBus.$emit('toast-failure', '질문지 삭제', getErrorMessage(error, '질문지를 삭제하지 못했습니다.'));
            } finally {
                this.deleting_template_id = null;
            }
        },
        async copyTemplate(item) {
            if (this.copying_template_code !== null) return;
            this.copying_template_code = item.template_code;
            try {
                let formData = new FormData();
                formData.append('template_code', item.template_code);

                await this.$axios.post('/copy_template', formData);
                this.$EventBus.$emit('toast-success', '템플릿 복제', `"${item.template_name || item.template_code}" 를 복제했습니다.`);
                await this.readTemplates(this.user_id);
            } catch (error) {
                if (error?.response?.status === 401) return;
                this.$EventBus.$emit('toast-failure', '템플릿 복제', getErrorMessage(error, '템플릿을 복제하지 못했습니다.'));
            } finally {
                this.copying_template_code = null;
            }
        },
        newTemplate() {
            this.change_edit_mode(true);
            this.$router.push({name: 'EditingTemplate', query: {value_new: true}});
        },
        async parseCriteriaDefinition() {
            if (this.is_parsing_criteriaDefinition) return;
            const ok = await this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: '전체 질문지 답변 파싱',
                message: '전체 질문지의 업체 답변을 다시 파싱해 파서 값을 갱신합니다. 시간이 걸릴 수 있습니다.',
            });
            if (!ok) return;

            this.is_parsing_criteriaDefinition = true;
            try {
                const parsingDefinitionFormData = new FormData();
                parsingDefinitionFormData.append('type', 'figure');
                await this.$axios.post('/parser/values', parsingDefinitionFormData);
                this.$EventBus.$emit('toast-success', '파싱 완료', '전체 질문지 답변 파싱 및 저장을 마쳤습니다.');
            } catch (error) {
                if (error?.response?.status === 401) return;
                this.$EventBus.$emit('toast-failure', '파싱 실패', getErrorMessage(error, '질문지 답변 파싱 및 저장 중 오류가 발생했습니다.'));
            } finally {
                this.is_parsing_criteriaDefinition = false;
            }
        },
        checkAllowed(campaign_type, user_admin_level) {
            if (user_admin_level >= 4) return true;
            if (campaign_type == 'owner') return true;

            return false;
        }
    }
};
</script>

<style>
/* 고정 열 합(No. 56 · 최종편집일 168 · 관리 156 = 380) + 템플릿명 · 연결된 캠페인 각 200 — 좁으면 가로 스크롤. 본문 폭(1366 에서 1110) 안에 든다 */
.template-list-table {
    min-width: 780px;
}

/* 첫 캠페인 이름이 '외 N개' 옆에서 줄어들게 */
.template-list-table .template-campaign-name {
    min-width: 0;
}

/* b-link 비활성(.disabled)도 아이콘 버튼 :disabled 와 같게 */
.template-list-table .ds-row-icon-btn.disabled {
    opacity: .5;
    pointer-events: none;
}
</style>
